import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCog, KeyRound, Save } from "lucide-react";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {};
      if (name && name !== user.name) payload.name = name.trim();
      if (email && email.toLowerCase() !== user.email) payload.email = email.trim().toLowerCase();
      if (!payload.name && !payload.email) {
        toast.info("Değişiklik yok");
        setSavingProfile(false);
        return;
      }
      const { data } = await api.patch("/auth/profile", payload);
      setUser(data.user);
      toast.success("Profil güncellendi");
    } catch (err) {
      toast.error("Güncellenemedi", { description: formatApiError(err) });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Yeni parolalar eşleşmiyor");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Yeni parola en az 8 karakter olmalı");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Parola değiştirildi");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error("Parola değiştirilemedi", { description: formatApiError(err) });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div>
      <PageHeader subtitle="Kişisel hesap ayarları" title="Profil" />
      <div className="max-w-[900px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile card */}
        <div className="border border-zinc-200 bg-white p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-10 w-10 rounded-sm bg-zinc-950 text-white flex items-center justify-center">
              <UserCog className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight">Hesap bilgileri</div>
              <div className="text-sm text-zinc-500 mt-1">Ad ve e-posta adresi.</div>
            </div>
          </div>
          <form onSubmit={saveProfile} className="space-y-3" data-testid="profile-form">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Ad Soyad
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1"
                data-testid="profile-name-input"
                required
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                E-posta
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1 font-mono"
                data-testid="profile-email-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={savingProfile}
              className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white h-10"
              data-testid="profile-save-btn"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {savingProfile ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </form>
        </div>

        {/* Password card */}
        <div className="border border-zinc-200 bg-white p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-10 w-10 rounded-sm bg-[#002FA7] text-white flex items-center justify-center">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight">Parolayı değiştir</div>
              <div className="text-sm text-zinc-500 mt-1">
                En az 8 karakter. Değişikliğin ardından mevcut oturum açık kalır.
              </div>
            </div>
          </div>
          <form onSubmit={changePw} className="space-y-3" data-testid="password-form">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Mevcut parola
              </Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1 font-mono"
                required
                data-testid="password-current-input"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Yeni parola
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1 font-mono"
                minLength={8}
                required
                data-testid="password-new-input"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Yeni parola (tekrar)
              </Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1 font-mono"
                minLength={8}
                required
                data-testid="password-confirm-input"
              />
            </div>
            <Button
              type="submit"
              disabled={savingPw}
              className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white h-10"
              data-testid="password-change-btn"
            >
              <KeyRound className="h-4 w-4 mr-1.5" />
              {savingPw ? "Değiştiriliyor…" : "Parolayı değiştir"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
